<template>
  <div class="ms-safe-html" :class="[className, {error: errorMessage}]">
    <div v-if="content" ref="contentContainer"></div>
    <div v-else class="empty-content">{{ emptyText }}</div>
    
    <div v-if="errorMessage" class="error-text">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch, onMounted, nextTick } from 'vue';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import xssUtils from '@/commons/xssUtils';
import { useValidateControl } from '@/composables/validateControl.js';

export default defineComponent({
  name: 'MsSafeHtml',
  extends: msBaseComponent,
  props: {
    /**
     * Nội dung HTML cần hiển thị
     */
    content: {
      type: String,
      default: ''
    },
    /**
     * Các tag HTML được phép - nếu không chỉ định sẽ dùng danh sách mặc định
     * Ví dụ: ['a', 'p', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li']
     */
    allowedTags: {
      type: Array,
      default: null
    },
    /**
     * Các thuộc tính được phép - nếu không chỉ định sẽ dùng danh sách mặc định
     * Ví dụ: ['href', 'target', 'class', 'id']
     */
    allowedAttrs: {
      type: Array,
      default: null
    },
    /**
     * Text hiển thị khi không có nội dung
     */
    emptyText: {
      type: String,
      default: ''
    },
    /**
     * Class tùy chỉnh cho container
     */
    className: {
      type: String,
      default: ''
    },
    /**
     * Có sử dụng hook khi click vào các phần tử trong nội dung
     */
    useClickHandler: {
      type: Boolean,
      default: false
    },
    /**
     * Có sanitize nội dung hay không
     */
    sanitize: {
      type: Boolean,
      default: true
    }
  },
  emits: ['contentClick', 'linkClick'],
  setup(props, { emit }) {
    const contentContainer = ref(null);
    const { errorMessage, validate } = useValidateControl({ props });

    // Cập nhật nội dung HTML
    const updateContent = () => {
      if (!contentContainer.value) return;
      
      // Nếu không có nội dung, xóa trắng
      if (!props.content) {
        contentContainer.value.innerHTML = '';
        return;
      }
      
      // Nếu yêu cầu sanitize thì xử lý, nếu không thì hiển thị nguyên bản (nguy hiểm)
      if (props.sanitize) {
        // Tạo config tùy chỉnh nếu có các prop riêng
        const config = {};
        if (props.allowedTags) config.ALLOWED_TAGS = props.allowedTags;
        if (props.allowedAttrs) config.ALLOWED_ATTR = props.allowedAttrs;
        
        contentContainer.value.innerHTML = xssUtils.sanitizeHtml(props.content, config);
      } else {
        contentContainer.value.innerHTML = props.content;
      }
      
      // Thêm handler cho các link nếu cần
      if (props.useClickHandler) {
        addClickHandlers();
      }
    };

    // Thêm handler click cho các phần tử
    const addClickHandlers = () => {
      if (!contentContainer.value) return;
      
      // Handler cho toàn bộ nội dung
      contentContainer.value.addEventListener('click', (event) => {
        emit('contentClick', event);
      });
      
      // Handler riêng cho các link
      const links = contentContainer.value.querySelectorAll('a');
      links.forEach(link => {
        link.addEventListener('click', (event) => {
          // Sanitize URL
          const href = link.getAttribute('href');
          const safeUrl = xssUtils.sanitizeUrl(href);
          
          // Nếu URL không an toàn, ngăn chặn chuyển hướng
          if (safeUrl === '#' && href !== '#') {
            event.preventDefault();
          }
          
          emit('linkClick', {
            event,
            element: link,
            url: safeUrl,
            originalUrl: href,
            text: link.textContent
          });
        });
      });
    };

    // Theo dõi thay đổi của nội dung
    watch(() => props.content, () => {
      nextTick(() => {
        updateContent();
      });
    });

    // Cập nhật khi component được mount
    onMounted(() => {
      updateContent();
    });

    return {
      contentContainer,
      errorMessage,
      validate
    };
  }
});
</script>

<style lang="scss" scoped>
.ms-safe-html {
  .empty-content {
    color: var(--color-text-inactive);
    font-style: italic;
  }
  
  &.error {
    border-color: var(--color-danger);
  }
  
  .error-text {
    color: var(--color-danger);
    font-size: 12px;
    margin-top: 4px;
  }
}
</style>
